import { BrowserRouter as Router } from 'react-router';
import { ThemeProvider, CssBaseline, Box, Stack, Toolbar } from '@mui/material';
import { AppRoutes } from './router/app-routes';
import './i18n';
import { getThemeConfig } from './config/theme';
import { AuthProvider, hasAuthParams } from 'react-oidc-context';
import { getOidcConfig } from './config/keycloak';
import { Layout } from './modules/layout/layout';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './api/query-client';
import { ErrorBoundary, LoadingSpinner } from './components';
import type { EnvironmentData } from './types/environment.types';
import { SnackbarProvider } from './context/snackbar/snackbar.provider';
import { useThemeStore } from './store/theme';
import { useEffect, useRef } from 'react';
import { useAuth } from './hooks/use-auth';
import { useTranslation } from 'react-i18next';
import { setupAxiosInterceptors } from './config/axios';

declare global {
  interface Window {
    configuration: EnvironmentData;
  }
}

const oidcConfig = getOidcConfig();

const AppContent = () => {
  const { t } = useTranslation();
  const { isDarkMode, operator } = useThemeStore();
  const theme = getThemeConfig(isDarkMode, operator);
  const hasTriedSignin = useRef(false);
  const { auth } = useAuth();

  useEffect(() => {
    setupAxiosInterceptors(
      () => auth.user?.access_token,
      async () => {
        await auth.signinSilent();
      },
      () => auth.removeUser(),
    );
  }, [auth]);

  // automatically sign-in
  useEffect(() => {
    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !hasTriedSignin.current
    ) {
      auth.signinSilent().catch((error) => {
        console.log('Silent authentication failed - showing login page', error.message);
        auth.removeUser();
      });
      hasTriedSignin.current = true;
    }
  }, [auth]);

  if (auth.isLoading) return <LoadingSpinner message={t('common.loading')} />;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <Router>
          <Stack minHeight="100vh" width="100%">
            <Layout />
            <Toolbar />
            <Box sx={{ flex: 1 }}>
              <AppRoutes />
            </Box>
          </Stack>
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <AuthProvider {...oidcConfig}>
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider>
          <AppContent />
        </SnackbarProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
};
export default App;
